import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, ArrowLeft, Send, CheckCircle, AlertCircle, Loader2, MapPin, ScanLine, Download, RefreshCw } from 'lucide-react';
import MapEditor from '../components/maps/MapEditor';
import ScannerOverlay from '../components/ui/ScannerOverlay';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { renderRichText, isHttpUrl } from '../lib/richText';
import DataSourcesTab from './DataSourcesTab';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface DataField {
  id: string;
  name: string;
  type: string;
  description?: string;
}

interface QuestionnaireField {
  id: string;
  dataFieldId: string;
  dataField: DataField;
  label?: string;
  required: boolean;
  sortOrder: number;
}

interface Questionnaire {
  id: string;
  name: string;
  description?: string;
  organization?: { name: string };
  fields: QuestionnaireField[];
  deadline?: string;
  status: string;
  afterSubmitAction?: 'SHOW_READONLY' | 'REDIRECT' | 'REFILL' | 'THANKS_TEXT';
  pdfEnabled?: boolean;
  redirectUrl?: string | null;
  thanksText?: string | null;
}

function formatAnswerValue(raw?: string): string {
  if (!raw) return '—';
  if (raw.startsWith('{') || raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) return '—';
        return parsed
          .map((item: any, index: number) => {
            const name = item?.name || item?.description || `Punkt ${index + 1}`;
            const hasCoords = item?.lat !== undefined || item?.lon !== undefined;
            return hasCoords ? `${name} (${item.lat ?? ''}, ${item.lon ?? ''})` : name;
          })
          .join('\n');
      }
      if (parsed && typeof parsed === 'object') {
        if (parsed.value !== undefined) return String(parsed.value);
        return Object.entries(parsed).map(([key, value]) => `${key}: ${value}`).join('\n');
      }
    } catch {
      // Rohwert anzeigen
    }
  }
  return raw;
}

const fieldTypeLabels: Record<string, string> = {
  TEXT: 'Text', NUMBER: 'Zahl', BOOLEAN: 'Ja/Nein', DATE: 'Datum',
  POI: 'POI', POI_LIST: 'POI-Liste', COORDINATE_PAIR: 'Koordinaten', SELECT: 'Auswahl',
};

// --- POI_LIST Editor with Karte + Drag-and-Drop ---
function PoIListEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [items, setItems] = React.useState<any[]>(() => {
    try { const p = JSON.parse(value || "[]"); return Array.isArray(p) ? p : []; } catch { return []; }
  });
  const [showMap, setShowMap] = React.useState(false);
  const [dragIdx, setDragIdx] = React.useState<number | null>(null);
  const mapRef = React.useRef<L.Map | null>(null);
  const mapElRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => { onChange(JSON.stringify(items)); }, [items]);

  React.useEffect(() => {
    if (!showMap || !mapElRef.current || mapRef.current) return;
    const valid = items.filter(i => i.lat && i.lon && i.lat !== 0 && i.lon !== 0);
    if (valid.length === 0) return;

    const map = L.map(mapElRef.current, { zoomControl: true, attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    mapRef.current = map;

    const bounds: [number, number][] = [];
    valid.forEach((item: any) => {
      const ll: [number, number] = [item.lat, item.lon];
      bounds.push(ll);
      L.marker(ll).addTo(map).bindPopup("<b>" + (item.name || "") + "</b>" + (item.description ? "<br/>" + item.description : ""));
    });
    if (bounds.length > 0) map.fitBounds(bounds, { padding: [30, 30] });

    return () => { mapRef.current = null; map.remove(); };
  }, [showMap, items]);

  const updateItem = (idx: number, patch: any) => {
    const c = [...items]; c[idx] = { ...c[idx], ...patch }; setItems(c);
  };

  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} draggable
          onDragStart={() => setDragIdx(idx)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (dragIdx === null || dragIdx === idx) return;
            const c = [...items]; const [m] = c.splice(dragIdx, 1); c.splice(idx, 0, m); setItems(c);
            setDragIdx(null);
          }}
          onDragEnd={() => setDragIdx(null)}
          className={"border rounded-lg p-3 bg-gray-50 space-y-2 transition-opacity " + (dragIdx === idx ? "opacity-50 border-dashed border-primary" : "border-gray-200")}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="cursor-grab text-gray-400 select-none" title="Zum Sortieren ziehen">☰</span>
              <span className="text-xs font-medium text-gray-500">POI #{idx + 1}</span>
            </div>
            <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))}
              className="btn-ghost text-xs text-red-500 p-0.5">🗑</button>
          </div>
          <input type="text" className="input-field" placeholder="Name *" value={item.name || ""}
            onChange={(e) => updateItem(idx, { name: e.target.value })} />
          <input type="text" className="input-field" placeholder="Beschreibung" value={item.description || ""}
            onChange={(e) => updateItem(idx, { description: e.target.value })} />
          <div className="flex gap-2">
            <input type="number" step="any" className="input-field flex-1" placeholder="Breitengrad" value={item.lat || ""}
              onChange={(e) => updateItem(idx, { lat: parseFloat(e.target.value) || 0 })} />
            <input type="number" step="any" className="input-field flex-1" placeholder="Längengrad" value={item.lon || ""}
              onChange={(e) => updateItem(idx, { lon: parseFloat(e.target.value) || 0 })} />
            <button type="button" onClick={() => {
              if (!navigator.geolocation) { alert("GPS nicht unterstützt"); return; }
              navigator.geolocation.getCurrentPosition(
                (pos) => updateItem(idx, { lat: pos.coords.latitude, lon: pos.coords.longitude }),
                () => alert("GPS-Position konnte nicht ermittelt werden"),
                { enableHighAccuracy: true, timeout: 10000 }
              );
            }} className="btn-secondary text-xs px-2 py-2 shrink-0" title="Aktuelle Position"><MapPin size={16} className="text-blue-500" /></button>
          </div>
        </div>
      ))}

      <div className="flex gap-2">
        <button type="button" onClick={() => setItems([...items, { name: "", description: "", lat: 0, lon: 0 }])}
          className="btn-secondary text-sm flex-1 flex items-center justify-center gap-1">
          + POI hinzufügen
        </button>
        {items.filter(i => i.lat && i.lon && i.lat !== 0 && i.lon !== 0).length > 0 && (
          <button type="button" onClick={() => setShowMap(!showMap)}
            className={"btn-secondary text-sm px-3 " + (showMap ? "bg-primary/10 text-primary border-primary/30" : "")}>
            {showMap ? "Karte ✖" : "🗺 Karte"}
          </button>
        )}
      </div>

      {showMap && (
        <div ref={mapElRef} className="w-full h-64 rounded-lg border border-gray-300 overflow-hidden z-0" style={{ minHeight: "256px" }} />
      )}
    </div>
  );
}


export default function FillQuestionnairePage({ questionnaireId }: { questionnaireId?: string } = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = questionnaireId ?? routeId;
  const navigate = useNavigate();
  const { user } = useAuth();

  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submission, setSubmission] = useState<any>(null);
  const [error, setError] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [scanFieldId, setScanFieldId] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showSources, setShowSources] = useState(false);
  const isEditor = user?.role === 'USER_EDITOR' || user?.role?.startsWith('ADMIN_');
  
  const { data: questionnaire, isLoading, isError } = useQuery<Questionnaire>({
    queryKey: ['questionnaire', id],
    queryFn: async () => {
      const res = await api.get(`/questionnaires/${id}`);
      return Array.isArray(res.data) ? res.data[0] : res.data;
    },
    enabled: !!id,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const fieldValues = Object.entries(values)
        .map(([fieldId, value]) => ({ fieldId, value }));
      return api.post(`/questionnaires/${id}/submit`, {
        submittedById: user?.id || 'system',
        values: fieldValues,
      });
    },
    onSuccess: (res) => {
      setSubmission(res.data);
      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
      queryClient.invalidateQueries({ queryKey: ['data-entries'] });
    },
    onError: (err: any) => setError(err.response?.data?.message || err.message),
  });

    // Auto-submit toggle (per questionnaire)
  const qc_auto = useQueryClient();
  const [autoSubmit, setAutoSubmit] = useState(questionnaire?.autoSubmit || false);
  useEffect(() => {
    if (questionnaire?.autoSubmit !== undefined) setAutoSubmit(questionnaire.autoSubmit);
  }, [questionnaire?.autoSubmit]);

  const toggleAutoSubmit = useMutation({
    mutationFn: async (val: boolean) => {
      await api.patch('/questionnaires/' + id + '/auto-submit', { autoSubmit: val });
    },
    onSuccess: () => { qc_auto.invalidateQueries({ queryKey: ['questionnaire', id] }); },
  });

  const afterSubmitAction = questionnaire?.afterSubmitAction ?? 'REFILL';
  const redirectTarget =
    afterSubmitAction === 'REDIRECT' && isHttpUrl(questionnaire?.redirectUrl)
      ? (questionnaire!.redirectUrl as string)
      : null;

  useEffect(() => {
    if (submitted && redirectTarget) {
      window.location.replace(redirectTarget);
    }
  }, [submitted, redirectTarget]);

  const submittedEntryId = submission?.entryId || submission?.entry?.id;

  const downloadPdf = async () => {
    if (!id || !submittedEntryId) return;
    setPdfLoading(true);
    setPdfError('');
    try {
      const res = await api.get(`/questionnaires/${id}/entries/${submittedEntryId}/pdf`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `einreichung-${submittedEntryId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setPdfError(err.response?.data?.message || 'PDF konnte nicht erstellt werden');
    } finally {
      setPdfLoading(false);
    }
  };

  const resetForm = () => {
    setValues({});
    setSubmission(null);
    setError('');
    setPdfError('');
    setSubmitted(false);
  };

if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !questionnaire) {
    return (
      <div className="card text-center py-12">
        <AlertCircle size={40} className="mx-auto text-red-400 mb-3" />
        <p className="text-red-500">Fragebogen nicht gefunden</p>
        <button onClick={() => navigate('/')} className="btn-secondary mt-4 text-sm">Zurück zur Übersicht</button>
      </div>
    );
  }

  const sortedFields = [...(questionnaire.fields || [])].sort((a, b) => a.sortOrder - b.sortOrder);

  if (submitted) {
    // Weiterleitung: Der Effekt oben übernimmt den eigentlichen Wechsel.
    if (redirectTarget) {
      return (
        <div className="max-w-lg mx-auto mt-12">
          <div className="card text-center py-12 space-y-3">
            <Loader2 size={32} className="mx-auto animate-spin text-primary" />
            <p className="text-sm text-gray-500">Du wirst weitergeleitet …</p>
            <a href={redirectTarget} className="text-sm text-primary underline break-all">{redirectTarget}</a>
          </div>
        </div>
      );
    }

    // Nur der formatierte Dankestext des Erstellers
    if (afterSubmitAction === 'THANKS_TEXT') {
      return (
        <div className="max-w-lg mx-auto mt-12">
          <div className="card px-8 py-10">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle size={40} className="text-green-500 shrink-0" />
              <h2 className="text-xl font-bold text-gray-900">Vielen Dank!</h2>
            </div>
            <div
              className="text-sm text-gray-700 space-y-2 [&_a]:text-primary [&_a]:underline [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              dangerouslySetInnerHTML={{ __html: renderRichText(questionnaire.thanksText) }}
            />
          </div>
        </div>
      );
    }

    // Antworten unveränderbar anzeigen (optional mit PDF)
    if (afterSubmitAction === 'SHOW_READONLY') {
      return (
        <div className="max-w-2xl mx-auto mt-8 space-y-4">
          <div className="card">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center gap-3">
              <CheckCircle size={28} className="text-green-500 shrink-0" />
              <div>
                <h2 className="text-lg font-bold text-gray-900">Eingereicht!</h2>
                <p className="text-sm text-gray-500">
                  Deine Antworten für <strong>{questionnaire.name}</strong> wurden übermittelt.
                </p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {sortedFields.length === 0 ? (
                <p className="text-sm text-gray-400">Dieser Fragebogen hat keine Felder.</p>
              ) : (
                sortedFields.map((qf) => (
                  <div key={qf.id}>
                    <p className="text-xs font-medium text-gray-500">
                      {qf.label || qf.dataField?.label || qf.dataField?.name || 'Feld'}
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-gray-800">
                      {formatAnswerValue(values[qf.dataFieldId])}
                    </p>
                  </div>
                ))
              )}

              {pdfError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  {pdfError}
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-100">
                {questionnaire.pdfEnabled && submittedEntryId && (
                  <button onClick={downloadPdf} disabled={pdfLoading} className="btn-secondary">
                    {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    PDF herunterladen
                  </button>
                )}
                <button onClick={() => navigate('/')} className="btn-primary">
                  Zurück zur Übersicht
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // REFILL: bisheriges Verhalten, Formular kann erneut ausgefüllt werden
    return (
      <div className="max-w-lg mx-auto mt-12">
        <div className="card text-center py-12 space-y-4">
          <CheckCircle size={48} className="mx-auto text-green-500" />
          <h2 className="text-xl font-bold text-gray-900">Eingereicht! 🎉</h2>
          <p className="text-sm text-gray-500">
            Deine Antworten für <strong>{questionnaire.name}</strong> wurden erfolgreich übermittelt.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={resetForm} className="btn-primary">
              <RefreshCw size={16} /> Erneut ausfüllen
            </button>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Zurück zur Übersicht
            </button>
          </div>
        </div>
      </div>
    );
  }

  const renderFieldInput = (qf: QuestionnaireField) => {
    const field = qf.dataField || {};
    const val = values[qf.dataFieldId] ?? '';
    const setVal = (v: string) => setValues(prev => ({ ...prev, [qf.dataFieldId]: v }));

    switch (field.type) {
      case 'NUMBER':
        return (
          <input
            type="number"
            className="input-field"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="Zahl eingeben..."
            required={qf.required}
          />
        );
      case 'BOOLEAN':
        return (
          <select className="input-field" value={val} onChange={(e) => setVal(e.target.value)} required={qf.required}>
            <option value="">Bitte wählen</option>
            <option value="true">Ja</option>
            <option value="false">Nein</option>
          </select>
        );
      case 'DATE':
        return (
          <input
            type="date"
            className="input-field"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            required={qf.required}
          />
        );
      case 'COORDINATE_PAIR':
        return (
          <div className="flex gap-2 items-start">
            <div className="flex gap-2 flex-1">
              <input
                type="number"
                step="any"
                className="input-field flex-1"
                placeholder="Latitude"
                value={val.split(',')[0] || ''}
                onChange={(e) => {
                  const [, lng] = val.split(',');
                  setVal(`${e.target.value},${lng || ''}`);
                }}
                required={qf.required}
              />
              <input
                type="number"
                step="any"
                className="input-field flex-1"
                placeholder="Longitude"
                value={val.split(',')[1] || ''}
                onChange={(e) => {
                  const [lat] = val.split(',');
                  setVal(`${lat || ''},${e.target.value}`);
                }}
                required={qf.required}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                if (!navigator.geolocation) { alert('GPS wird nicht unterstutzt'); return; }
                navigator.geolocation.getCurrentPosition(
                  (pos) => setVal(pos.coords.latitude.toFixed(6) + ',' + pos.coords.longitude.toFixed(6)),
                  () => alert('GPS-Position konnte nicht ermittelt werden'),
                  { enableHighAccuracy: true, timeout: 10000 }
                );
              }}
              className="btn-secondary text-xs px-2 py-2 shrink-0"
              title="Aktuelle Position"
            >
              <MapPin size={16} className="text-blue-500" />
            </button>
          </div>
        );
      case 'POI_LIST':
        return <PoIListEditor value={val} onChange={(v) => setVal(v)} />;
      case 'POI':
        let poiName = '', poiDesc = '', poiLat = '', pooLon = '';
        try { const p = JSON.parse(val); poiName = p.name || ''; poiDesc = p.description || ''; poiLat = String(p.lat || ''); pooLon = String(p.lon || ''); } catch {}
        return (
          <div className="space-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <input type="text" className="input-field" placeholder="Name *" value={poiName}
              onChange={(e) => setVal(JSON.stringify({ name: e.target.value, description: poiDesc, lat: parseFloat(poiLat) || 0, lon: parseFloat(pooLon) || 0 }))} />
            <input type="text" className="input-field" placeholder="Beschreibung" value={poiDesc}
              onChange={(e) => setVal(JSON.stringify({ name: poiName, description: e.target.value, lat: parseFloat(poiLat) || 0, lon: parseFloat(pooLon) || 0 }))} />
            <div className="flex gap-2">
              <input type="number" step="any" className="input-field flex-1" placeholder="Breitengrad" value={poiLat}
                onChange={(e) => setVal(JSON.stringify({ name: poiName, description: poiDesc, lat: parseFloat(e.target.value) || 0, lon: parseFloat(pooLon) || 0 }))} />
              <input type="number" step="any" className="input-field flex-1" placeholder="Längengrad" value={pooLon}
                onChange={(e) => setVal(JSON.stringify({ name: poiName, description: poiDesc, lat: parseFloat(poiLat) || 0, lon: parseFloat(e.target.value) || 0 }))} />
              <button type="button" onClick={() => {
                if (!navigator.geolocation) { alert('GPS nicht unterstützt'); return; }
                navigator.geolocation.getCurrentPosition(
                  (pos) => setVal(JSON.stringify({ name: poiName, description: poiDesc, lat: pos.coords.latitude, lon: pos.coords.longitude })),
                  () => alert('GPS-Position konnte nicht ermittelt werden'),
                  { enableHighAccuracy: true, timeout: 10000 }
                );
              }} className="btn-secondary text-xs px-2 py-2 shrink-0" title="Aktuelle Position"><MapPin size={16} className="text-blue-500" /></button>
            </div>
          </div>
        );

      case 'GEOJSON':
        return <MapEditor value={val} onChange={(v) => setVal(v)} />;
      case 'TEXT':
      default:
        return (
          <div className="flex gap-2 items-start">
            <input
              type="text"
              className="input-field flex-1"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder="Text eingeben..."
              required={qf.required}
            />
            <button
              type="button"
              onClick={() => setScanFieldId(qf.dataFieldId)}
              className="btn-secondary text-xs px-2 py-2 shrink-0"
              title="Code scannen"
            >
              <ScanLine size={16} className="text-purple-500" />
            </button>
          </div>
        );
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button onClick={() => navigate('/')} className="btn-ghost p-2 -ml-2 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={16} /> Zurück
      </button>

      {isEditor && (
        <div className="flex gap-1 border-b border-gray-200">
          <button onClick={() => setShowSources(false)}
            className={'pb-2 px-3 text-sm font-medium border-b-2 transition-colors ' + (!showSources ? 'border-primary text-primary' : 'border-transparent text-gray-500')}>
            Ausfüllen
          </button>
          <button onClick={() => setShowSources(true)}
            className={'pb-2 px-3 text-sm font-medium border-b-2 transition-colors ' + (showSources ? 'border-primary text-primary' : 'border-transparent text-gray-500')}>
            Datenquellen
          </button>
        </div>
      )}

      {showSources && isEditor ? (
        <div className="card p-4 space-y-4">
          <div className="border-b border-gray-200 pb-3">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              Datenquellen konfigurieren
            </h3>
          </div>
          <DataSourcesTab questionnaireId={questionnaire.id} userOrgId={user?.organizationId || ''} fields={questionnaire.fields || []} />
        </div>
      ) : (
        <div className="card">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <ClipboardList size={24} className="text-primary" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{questionnaire.name}</h1>
              {questionnaire.organization?.name && (
                <p className="text-xs text-gray-500">{questionnaire.organization.name}</p>
              )}
            </div>
          </div>
          {questionnaire.description && (
            <p className="mt-2 text-sm text-gray-600">{questionnaire.description}</p>
          )}
          {questionnaire.deadline && (
            <p className="mt-1 text-xs text-accent-orange">
              ⏰ Frist: {format(new Date(questionnaire.deadline), 'dd.MM.yyyy HH:mm', { locale: de })} Uhr
            </p>
          )}
          {isEditor && (
            <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer border-t border-gray-100 pt-3">
              <input type="checkbox" checked={autoSubmit}
                onChange={(e) => { setAutoSubmit(e.target.checked); toggleAutoSubmit.mutate(e.target.checked); }}
                className="checkbox" />
              <span>Automatisch absenden <span className="text-xs text-gray-400">(bei Aktualisierung aggregierter Daten)</span></span>
            </label>
          )}
        </div>

        <div className="p-6 space-y-5">
          {sortedFields.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">
              Dieser Fragebogen hat noch keine Felder.
            </div>
          ) : (
            sortedFields.map((qf) => (
              <div key={qf.id}>
                <label className="label flex items-center gap-1">
                  {qf.label || qf.dataField?.label || qf.dataField?.name || 'Feld'}
                  {qf.required && <span className="text-red-500">*</span>}
                </label>
                {qf.dataField?.description && (
                  <p className="text-xs text-gray-400 mb-1">{qf.dataField.description}</p>
                )}
                <div className="flex items-center gap-2">
                  <span className="badge-info text-[10px] shrink-0">
                    {fieldTypeLabels[qf.dataField?.type] || qf.dataField?.type || '?'}
                  </span>
                  <div className="flex-1">
                    {renderFieldInput(qf)}
                  </div>
                </div>
              </div>
            ))
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <div className="pt-4 border-t border-gray-100">
            <button
              onClick={() => submitMutation.mutate()}
              disabled={submitMutation.isPending || sortedFields.length === 0}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {submitMutation.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
              Einreichen
            </button>
          </div>
        </div>
      </div>
      )}
      {scanFieldId && (
        <ScannerOverlay
          onScan={(text) => {
            setValues(prev => ({ ...prev, [scanFieldId]: text }));
            setScanFieldId(null);
          }}
          onClose={() => setScanFieldId(null)}
        />
      )}
    </div>
  );
}
